import { motion } from 'framer-motion';
import { Mail, MapPin, Globe, Github, Linkedin, Twitter, Star, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { personalData } from '../data/personal';
import { useState, useEffect, useRef } from 'react';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';

const ContactPage = () => {
  const socialIcons = {
    github: Github,
    linkedin: Linkedin,
    twitter: Twitter,
  };

  // Language Skills Carousel
  const [langCurrentSlide, setLangCurrentSlide] = useState(0);
  const [langIsDragging, setLangIsDragging] = useState(false);
  const langAutoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [langSliderRef, langInstanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    rubberband: true,
    drag: true,
    mode: 'free-snap',
    slides: {
      perView: 1,
      spacing: 24,
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: { perView: 2, spacing: 24 },
      },
    },
    dragStarted: () => setLangIsDragging(true),
    dragEnded: () => setLangIsDragging(false),
    slideChanged(s) {
      setLangCurrentSlide(s.track.details.rel);
    },
  });

  useEffect(() => {
    if (!langInstanceRef.current) return;
    if (langAutoplayRef.current) clearInterval(langAutoplayRef.current);
    langAutoplayRef.current = setInterval(() => {
      langInstanceRef.current?.next();
    }, 4000);
    return () => {
      if (langAutoplayRef.current) clearInterval(langAutoplayRef.current);
    };
  }, [langInstanceRef]);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12"
      >
        <h1 className="text-4xl font-bold mb-4">Contact</h1>
        <p className="text-xl text-muted-foreground">
          Get in touch with me for collaborations, opportunities, or just to say hello.
        </p>
      </motion.div>

      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-semibold mb-6">Get In Touch</h2>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <Mail size={20} className="text-primary-600" />
                </div>
                <div>
                  <h3 className="font-medium">Email</h3>
                  <a 
                    href={`mailto:${personalData.email}`}
                    className="text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    {personalData.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <MapPin size={20} className="text-primary-600" />
                </div>
                <div>
                  <h3 className="font-medium">Location</h3>
                  <p className="text-muted-foreground">{personalData.location}</p>
                </div>
              </div>

              {personalData.website && (
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                    <Globe size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Website</h3>
                    <a 
                      href={personalData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {personalData.website}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-2xl font-semibold mb-6">Connect With Me</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {personalData.social.map((social, index) => {
                const IconComponent = socialIcons[social.icon as keyof typeof socialIcons];
                return (
                  <motion.a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    className="card hover:shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <div className="card-content flex items-center gap-4">
                      {IconComponent && (
                        <IconComponent size={24} className="text-primary-600" />
                      )}
                      <div>
                        <h3 className="font-medium">{social.platform}</h3>
                        <p className="text-sm text-muted-foreground">Follow me</p>
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Languages */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4"
            >
              <Users size={24} className="text-blue-600 dark:text-blue-400" />
            </motion.div>
            <h2 className="text-4xl font-bold mb-4">Language Skills</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
          </div>
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              aria-label="Previous language"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-blue-100 dark:hover:bg-blue-900 transition-all"
              onClick={() => langInstanceRef.current?.prev()}
              style={{ left: '-2.5rem' }}
            >
              <ChevronLeft size={24} />
            </button>
            <button
              aria-label="Next language"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-background border rounded-full shadow p-2 hover:bg-blue-100 dark:hover:bg-blue-900 transition-all"
              onClick={() => langInstanceRef.current?.next()}
              style={{ right: '-2.5rem' }}
            >
              <ChevronRight size={24} />
            </button>
            {/* Carousel */}
            <div
              ref={langSliderRef}
              className={`keen-slider ${langIsDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{ userSelect: langIsDragging ? 'none' : 'auto' }}
            >
              {personalData.languages.map((language, index) => (
                <div className="keen-slider__slide p-2" key={language.name}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                    className="card hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="card-content p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-lg">{language.name}</h3>
                        <span className="text-sm text-muted-foreground capitalize">{language.level}</span>
                      </div>
                      {/* Animated Progress Bar */}
                      <div className="relative">
                        <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-3">
                          <motion.div
                            className={`h-3 rounded-full shadow-lg relative overflow-visible`}
                            initial={{ width: 0 }}
                            animate={{ width: `${(language.stars / 5) * 100}%` }}
                            transition={{ duration: 1.5, delay: 0.8 + index * 0.2, ease: 'easeOut' }}
                            style={{
                              background: 'linear-gradient(90deg, #38bdf8 0%, #0ea5e9 70%, #0369a1 100%)',
                              boxShadow: '0 2px 8px 0 rgba(14,165,233,0.18), 0 1px 0 0 #fff6 inset'
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '100%',
                                height: '60%',
                                borderRadius: '8px',
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.05) 100%)',
                                pointerEvents: 'none',
                                zIndex: 1
                              }}
                            />
                          </motion.div>
                        </div>
                        {/* Stars */}
                        <div className="flex gap-1 mt-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={16}
                              className={`${
                                star <= language.stars 
                                  ? 'text-yellow-500 fill-current' 
                                  : 'text-secondary-300 dark:text-secondary-600'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {personalData.languages.map((_, idx) => (
                <button
                  key={idx}
                  aria-label={`Go to language slide ${idx + 1}`}
                  className={`w-3 h-3 rounded-full border-2 ${langCurrentSlide === idx ? 'bg-blue-600 border-blue-600' : 'bg-white dark:bg-background border-blue-300 dark:border-blue-700'} transition-all`}
                  onClick={() => langInstanceRef.current?.moveToIdx(idx)}
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* Hobbies & Interests */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-semibold mb-6">Hobbies & Interests</h2>
          <div className="flex flex-wrap gap-2">
            {personalData.hobbies.map((hobby, index) => (
              <motion.span
                key={hobby}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.05 }}
                className="bg-secondary-100 dark:bg-secondary-800 px-3 py-1 rounded-full text-sm"
              >
                {hobby}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage; 